import PropTypes from "prop-types";
import { Alert, Box, Chip, Skeleton, Typography } from "@mui/material";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import RegistryTagTable from "../../components/RegistryTagTable";

import { useGitLabRegistryTags } from "../../../../hooks/useGitLabRegistryTags";
import {
  FONT,
  HAIRLINE,
  ICON,
  INK,
  INK_MUTED,
  INK_SECONDARY,
  RADIUS,
  SURFACE,
  SURFACE_MUTED,
} from "../../../../theme/tokens";

const REGISTRY_DISPLAY_PREFIX = "registry.efabric.cs.wisc.edu/efabric/";
function stripDisplayPrefix(loc) {
  return loc?.startsWith(REGISTRY_DISPLAY_PREFIX)
    ? loc.slice(REGISTRY_DISPLAY_PREFIX.length)
    : (loc ?? "");
}

function SectionLabel() {
  return (
    <Typography
      sx={{ fontSize: FONT.sm, fontWeight: 600, color: INK_SECONDARY, mb: 1 }}
    >
      Container Images
    </Typography>
  );
}

export default function RegistryPanel({ projectId, showHeading = true }) {
  const { repositories, loading, error } = useGitLabRegistryTags(projectId);

  if (loading) {
    return (
      <Box sx={{ mt: 2 }}>
        {showHeading && <SectionLabel />}
        {Array.from({ length: 2 }).map((_, i) => (
          <Skeleton
            key={i}
            variant="rounded"
            height={36}
            sx={{ mb: 1, borderRadius: `${RADIUS.sm}px` }}
          />
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 2 }}>
        {showHeading && <SectionLabel />}
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2, mb: 3 }}>
      {showHeading && <SectionLabel />}

      {repositories.length === 0 ? (
        <Box
          sx={{
            border: `1px solid ${HAIRLINE}`,
            borderRadius: `${RADIUS.md}px`,
            py: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          <ImageOutlinedIcon sx={{ fontSize: 32, color: ICON }} />
          <Typography sx={{ fontSize: FONT.sm, color: INK_MUTED }}>
            No container images found for this project
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {repositories.map((repo) => (
            <Box
              key={repo.id}
              sx={{
                border: `1px solid ${HAIRLINE}`,
                borderRadius: `${RADIUS.md}px`,
                backgroundColor: SURFACE,
                overflow: "hidden",
              }}
            >
              {/* Repository header */}
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  backgroundColor: SURFACE_MUTED,
                  borderBottom: `1px solid ${HAIRLINE}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <ImageOutlinedIcon sx={{ fontSize: 16, color: ICON }} />
                <Typography
                  sx={{
                    fontFamily: "monospace",
                    fontSize: FONT.xs,
                    fontWeight: 600,
                    color: INK,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {stripDisplayPrefix(repo.location || repo.path)}
                </Typography>
                <Chip
                  label={`${repo.tags.length} tag${repo.tags.length !== 1 ? "s" : ""}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    ml: "auto",
                    height: 20,
                    fontSize: FONT.xs,
                    color: INK_SECONDARY,
                    borderColor: HAIRLINE,
                  }}
                />
              </Box>

              {/* Tags table */}
              <Box sx={{ px: 0.5, py: 0.5 }}>
                <RegistryTagTable
                  location={repo.location || repo.path}
                  tags={repo.tags.map((t) => ({
                    name: t.name,
                    size: t.total_size,
                    digest: t.digest,
                    createdAt: t.created_at,
                  }))}
                />
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

RegistryPanel.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  showHeading: PropTypes.bool,
};
